/**
 * The shopping list, in its own localStorage key.
 *
 * Its own key on purpose: the inventory is the thing that must never be
 * corrupted, and a list that gets rewritten in a shop has no business sharing a
 * record with it. It also means a future recipe-book update, or an inventory
 * migration, can never touch what someone wrote down to buy.
 *
 * Read-forgiving, write-loud — the same contract as every other store here. One
 * malformed line costs that line, not the list.
 */

import { StorageError } from "../../lib/storage/foodStorage";
import type { CartLine, CartSource } from "./types";

export const CART_KEY = "pantrymind.cart.v1";

const SOURCES: CartSource[] = ["recipe", "low", "typed"];
const MAX_NAME = 60;
/** A list longer than this is a bug or a bad paste, not a shop. */
const MAX_LINES = 200;

function toLine(value: unknown): CartLine | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;

  const id = record.id;
  const name = record.name;
  if (typeof id !== "string" || !id) return null;
  if (typeof name !== "string" || !name.trim() || name.length > MAX_NAME) return null;

  return {
    id,
    name: name.trim(),
    // An unknown source becomes "typed" rather than dropping the line: the
    // provenance is a nicety, the thing you need to buy isn't.
    source: SOURCES.includes(record.source as CartSource)
      ? (record.source as CartSource)
      : "typed",
    reason: typeof record.reason === "string" ? record.reason.slice(0, MAX_NAME) : undefined,
    got: record.got === true,
  };
}

export function loadCart(): CartLine[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const lines: CartLine[] = [];
    for (const entry of parsed) {
      if (lines.length >= MAX_LINES) break;
      const line = toLine(entry);
      if (line) lines.push(line);
    }
    return lines;
  } catch {
    return [];
  }
}

export function saveCart(lines: CartLine[]): void {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(lines.slice(0, MAX_LINES)));
  } catch {
    throw new StorageError("Couldn't save the list — your browser storage may be full.");
  }
}

export function clearCart(): void {
  saveCart([]);
}
